import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PageClickService } from '@radioking/shared/common-services';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { VideoPopoverComponent } from './video-popover.component';

export type PopoverPosition = 'top' | 'left' | 'bottom' | 'right';
export type PopoverProps = {
  title: string;
  description: string;
  src: string;
  thumb: string;
  knowledgeBaseUrl?: string;
  position?: PopoverPosition;
  style?: object;
};

@Directive({
  selector: '[rkVideoPopover]',
  standalone: true,
})
export class VideoPopoverDirective implements OnInit, OnDestroy {
  @Input() popoverTitle: string = '';
  @Input() popoverDescription: string = '';
  @Input() popoverPosition: PopoverPosition = 'top';
  @Input() props: PopoverProps;
  popoverRef: ComponentRef<VideoPopoverComponent> | null = null;
  subscription = new Subscription();

  constructor(
    private elRef: ElementRef,
    private appRef: ApplicationRef,
    private readonly pageclickService: PageClickService,
  ) {}

  @HostListener('click')
  onClick(): void {
    const mPopover = VideoPopoverComponent;
    if (this.popoverRef) {
      return;
    }
    this.popoverRef = createComponent(mPopover, {
      environmentInjector: this.appRef.injector,
    });
    const { fixedPopoverPosition } = this.getPopoverPosition();
    this.popoverRef.instance.setProperties({
      ...this.props,
      style: {
        ...this.getPopoverStyle(fixedPopoverPosition),
      },
    });

    document.body.appendChild(this.popoverRef.location.nativeElement);
    this.appRef.attachView(this.popoverRef.hostView);
  }

  ngOnInit(): void {
    this.clickOutside();
  }

  clickOutside() {
    this.subscription.add(
      this.pageclickService.documentClickedTarget
        .pipe(
          filter(
            (target: HTMLElement) =>
              (!target.closest('rk-video-popover') &&
                !target.closest('rk-popover-trigger')) ||
              !!target.closest('.close-popover'),
          ),
        )
        .subscribe((target: HTMLElement) => {
          this.popoverRef?.instance.destroyPopover();
          this.destroyPopover();
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroyPopover();
  }

  destroyPopover() {
    this.popoverRef?.instance.destroyPopover();
    setTimeout(() => {
      if (this.popoverRef !== null) {
        this.appRef.detachView(this.popoverRef.hostView);
        this.popoverRef.destroy();
        this.popoverRef = null;
      }
    }, 50);
  }

  // Check if the popover is going out of the screen
  getPopoverPosition() {
    const el = this.elRef.nativeElement.getBoundingClientRect();
    let fixedPopoverPosition = this.popoverPosition;
    if (this.popoverPosition === 'top' && el.top < 200) {
      fixedPopoverPosition = 'bottom';
    }
    if (
      this.popoverPosition === 'bottom' &&
      el.top + el.height + 90 > window.innerHeight
    ) {
      fixedPopoverPosition = 'top';
    }

    if (this.popoverPosition === 'left' && el.left < 200) {
      fixedPopoverPosition = 'right';
    }
    if (
      this.popoverPosition === 'right' &&
      el.left + el.height + 90 > window.innerWidth
    ) {
      fixedPopoverPosition = 'left';
    }

    return { fixedPopoverPosition: fixedPopoverPosition };
  }

  getPopoverStyle(fixedPopoverPosition: PopoverPosition): object {
    const el = this.elRef.nativeElement.getBoundingClientRect();
    let popoverStyle: { [k: string]: string } = {
      top: `${el.top + el.height / 2}px`,
      left: `${el.left + el.width / 2}px`,
    };
    return popoverStyle;
  }
}
